// D37: the translated title and plain-language explanation of a structured block reason, the decision labels and the
// question prefilled for the assistant. Pure (unit-tested): everything is built from {code, params}; the English
// status_reason is only shown under "Technical details".
import type { BlockDecision, BlockReason } from "@maestros/contracts";
import type { MessageKey } from "../../i18n/en.ts";
import type { Translate } from "../../i18n/index.tsx";
import { dependencyCauseText } from "../dependencies/causes.ts";

const key = (k: string): MessageKey => k as MessageKey;

export function blockTitle(t: Translate, reason: BlockReason | null): string {
  if (!reason) return t("block.title.unknown");
  if (reason.code === "environment") return t(key(`block.title.environment.${reason.params.kind}`));
  return t(key(`block.title.${reason.code}`));
}

const ESCALATION_CAUSES = new Set(["quality_gate_failed", "critic_rejected", "repeated_test_failure", "no_progress", "context_capacity", "capability_missing", "provider_unavailable", "quota_exhausted", "high_risk_policy", "human_override"]);

/** An engine cause (escalation reason or English stop cause) in the reader's language when known. */
export function causeText(t: Translate, cause: string | null): string {
  if (!cause) return t("block.unknownCause");
  if (ESCALATION_CAUSES.has(cause)) return t(key(`block.cause.${cause}`));
  if (/without acceptance|attempt ceiling/i.test(cause)) return t("block.cause.no_progress");
  if (/without material progress|no progress|stagnat|oscillation/i.test(cause)) return t("block.cause.no_progress");
  if (/deterministic gates failed|tests? failed/i.test(cause)) return t("block.cause.quality_gate_failed");
  if (/critic|Maestro/i.test(cause)) return t("block.cause.critic_rejected");
  return t("block.unknownCause");
}

/** Reasons of the rule-based council (packages/domain approval-questions.ts) in the reader's language; others verbatim. */
const COUNCIL_REASONS: [RegExp, MessageKey][] = [
  [/already approved one retry for this cause/i, "block.councilReason.retriedOnce"],
  [/repeated failure points to a defect/i, "block.councilReason.defect"],
  [/second retry would loop/i, "block.councilReason.loop"],
  [/linked fix task already carries the work/i, "block.councilReason.fixTask"],
  [/outside the agents' reach/i, "block.councilReason.outsideAgents"],
];

export function councilReasonText(t: Translate, reason: string): string {
  for (const [re, k] of COUNCIL_REASONS) if (re.test(reason)) return t(k);
  return reason;
}

function commandLabel(t: Translate, command: string, stage: string): string {
  if (stage === "integration") return t("block.command.integration");
  return t(key(`block.command.${command}`));
}

export function blockExplanation(t: Translate, reason: BlockReason | null, ctx: { numbers?: ReadonlyMap<string, number> } = {}): string {
  if (!reason) return t("block.explain.unknown");
  switch (reason.code) {
    case "environment": {
      const p = reason.params;
      return t(key(`block.explain.environment.${p.kind}`), {
        command: commandLabel(t, p.command, p.stage),
        binary: p.binary ?? t("block.unknownProgram"),
        exit: p.exit_code !== null ? t("block.exit", { code: p.exit_code }) : "",
      });
    }
    case "quality_after_premium":
    case "quality_stalled":
      return t(key(`block.explain.${reason.code}`), { cause: causeText(t, reason.params.cause) });
    case "council_denied": {
      const reasons = reason.params.reasons.map((r) => councilReasonText(t, r)).join(" ");
      return t("block.explain.council_denied", { previous: t(key(`block.title.${reason.params.previous_code ?? "unknown"}`)).toLowerCase(), reasons: reasons || t("block.reasonsNone") });
    }
    case "budget_limit":
      return t("block.explain.budget_limit", { limit: reason.params.limit_cents !== null ? t("block.limitAmount", { amount: `US$ ${(reason.params.limit_cents / 100).toFixed(2)}` }) : "" });
    case "unknown_cost":
      return t("block.explain.unknown_cost", { calls: reason.params.unknown_calls ?? "?" });
    case "run_ceiling":
      return t("block.explain.run_ceiling", { used: reason.params.used ?? "?", limit: reason.params.limit ?? "?", kind: t(key(`block.explain.run_ceiling.${reason.params.limit_kind}`)) });
    case "dependency_blocked": {
      const list = reason.params.blocking.map((b) => `#${b.number}`).join(", ");
      return t("block.explain.dependency_blocked", { list: list || "—" });
    }
    case "approval_needed":
      return t("block.explain.approval_needed", { code: reason.params.approval_code ?? "—" });
    case "integration_conflict":
      return t("block.explain.integration_conflict", {
        base: reason.params.base_branch ?? t("block.baseBranch"),
        files: reason.params.files.length > 0 ? t("block.inFiles", { files: reason.params.files.slice(0, 5).join(", ") }) : "",
      });
    case "fix_task_opened":
      return t("block.explain.fix_task_opened", { fix: reason.params.fix_task_number !== null ? `#${reason.params.fix_task_number}` : "—" });
    case "requirement_ambiguous":
      return t("block.explain.requirement_ambiguous", { detail: reason.params.detail ?? "—" });
    default:
      return t(key(`block.explain.${reason.code}`));
  }
}

export function decisionLabel(t: Translate, decision: BlockDecision, reason: BlockReason | null): string {
  if (decision === "ask_assistant" && reason?.code === "environment") return t("block.decision.ask_assistant.environment");
  return t(key(`block.decision.${decision}`));
}

/** The question the assistant receives when the owner clicks "Ask the assistant" (or "Split into smaller tasks"). */
export function assistantPrompt(t: Translate, reason: BlockReason | null, task: { number: number; title: string }, purpose: "ask" | "split" = "ask"): string {
  const title = task.title.slice(0, 120);
  if (purpose === "split") return t("block.prompt.split", { number: task.number, title });
  if (reason?.code === "environment") {
    return t("block.prompt.environment", { number: task.number, title, binary: reason.params.binary ?? t("block.unknownProgram"), exit: reason.params.exit_code !== null ? t("block.exitInline", { code: reason.params.exit_code }) : "" });
  }
  return t("block.prompt.default", { number: task.number, title });
}

const TEST_PROGRAM_MISSING = /the test command "([^"]*)" runs "([^"]+)", which is not available/;

/** A readiness cause the API writes in English, in the reader's language when it is a known one (D37 test_command). */
export function readinessCauseText(t: Translate, cause: string | null): string | null {
  if (!cause) return null;
  // D37.2: `test_command` may carry several findings joined by "; " — dependency findings start with a cause code.
  const segments = cause.split(/; (?=(?:prepare_not_configured|prepare_failed|package_manager_missing|registry_unreachable|package_manager_fallback): )/);
  return segments
    .map((segment) => {
      const m = TEST_PROGRAM_MISSING.exec(segment);
      if (m) return t("readiness.cause.testProgramMissing", { command: m[1] ?? "", program: m[2] ?? "" });
      return dependencyCauseText(t, segment) ?? segment;
    })
    .join("; ");
}

/** Open tasks that wait for `taskId` through the dependency chain (board cards carry depends_on). */
export function waitingOn(taskId: string, tasks: readonly { id: string; number: number; status: string; depends_on: readonly string[] }[]): { id: string; number: number }[] {
  const byDep = new Map<string, { id: string; number: number; status: string }[]>();
  for (const x of tasks) for (const d of x.depends_on) byDep.set(d, [...(byDep.get(d) ?? []), x]);
  const seen = new Set([taskId]);
  const out: { id: string; number: number }[] = [];
  const queue = [taskId];
  while (queue.length > 0) {
    for (const d of byDep.get(queue.shift() as string) ?? []) {
      if (seen.has(d.id)) continue;
      seen.add(d.id);
      if (d.status !== "done" && d.status !== "cancelled") out.push({ id: d.id, number: d.number });
      queue.push(d.id);
    }
  }
  return out;
}
